import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Keeps a single GstWebRTCAPI consumer connection and exposes:
 *   cameraMap { site : { cartId : { cameraName : producerId } } }
 *   getStream(producerId) -> Promise<MediaStream>
 */
export default function useRTSP(site = "barilan") {
  const [cameraMap, setCameraMap] = useState({});
  const apiRef      = useRef(null);     // GstWebRTCAPI
  const sessionsRef = useRef({});       // producerId -> consumerSession

  /*──────────────── bootstrap once ───────────────*/
  useEffect(() => {
    if (!site) return;            // nothing to do if site not known yet

    if (!window.GstWebRTCAPI) {
      console.error("GstWebRTCAPI bundle is missing on window");
      return;
    }

    const api = new window.GstWebRTCAPI({
      meta: { name: `Mgr-${site}-${Date.now()}` },
      signalingServerUrl: "wss://stream.carteav.com/ws/",
      iceServers: [
        {
          urls: "turn:peer.carteav.com:3478?transport=udp",
          username: "carteav",
          credential: "Carteav26",
        },
        { urls: "stun:stun.l.google.com:19302" },
      ],
    });
    api.setRole?.("consumer");
    apiRef.current = api;

      function merge(producer, add) {
            const { site: s, cartId, name: cameraName } = producer.meta || {};
            if (s !== site || !cartId || !cameraName) return;
        
            setCameraMap((prev) => {
              /* spread at every level so React gets new references */
              const cartsOfSite   = { ...(prev[s] || {}) };
              const camerasOfCart = { ...(cartsOfSite[cartId] || {}) };
        
              if (add) camerasOfCart[cameraName] = producer.id;
              else     delete camerasOfCart[cameraName];
        
              if (Object.keys(camerasOfCart).length === 0 && !add) {
                delete cartsOfSite[cartId];
              } else {
                cartsOfSite[cartId] = camerasOfCart;
              }
        
              return { ...prev, [s]: cartsOfSite };
            });
          }

    const listener = {
      producerAdded: (p) => merge(p, true),
      producerRemoved: (p) => merge(p, false),
    };

    api.registerProducersListener(listener);
    api.getAvailableProducers().forEach(listener.producerAdded);

    return () => {
      api.unregisterProducersListener(listener);
      Object.values(sessionsRef.current).forEach((s) => s.close?.());
      api.close?.();
    };
  }, [site]);

  /*──────────────── helper: producerId → MediaStream ───────────────*/
  const getStream = useCallback((producerId) => {
    return new Promise((resolve, reject) => {
      if (!producerId) return reject("no producerId");
      const api = apiRef.current;
      if (!api) return reject("API not ready");

      /* reuse if already open */
      const existing = sessionsRef.current[producerId];
      if (existing?.streams?.[0]) return resolve(existing.streams[0]);

      const session = api.createConsumerSession(producerId);
      if (!session) return reject("cannot createConsumerSession");
      sessionsRef.current[producerId] = session;
      session.mungeStereoHack = true;

      session.addEventListener("streamsChanged", () => {
        if (session.streams[0]) resolve(session.streams[0]);
      });
      session.addEventListener("error", reject);
      session.addEventListener("closed", () => {
        delete sessionsRef.current[producerId];
      });
      session.connect();
    });
  }, []);

  return { cameraMap, getStream };
}
